export type SharePlatform = 'whatsapp' | 'facebook' | 'telegram' | 'x' | 'qr' | 'copy_link';
export type ReferralType = 'invitar_amigo' | 'invitar_oyente' | 'invitar_radio' | 'invitar_locutor';
export type ReferralStatus = 'pendiente' | 'aceptada' | 'rechazada' | 'expirada';
export type RewardType = 'puntos' | 'insignia' | 'beneficio' | 'vip';

export interface SharePlatformConfig {
  id: SharePlatform;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  description: string;
}

export interface ReferralTypeConfig {
  id: ReferralType;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  description: string;
  scoreReward: number;
}

export interface ReferralRecord {
  id: string;
  referralType: ReferralType;
  invitedName: string;
  invitedEmail: string;
  status: ReferralStatus;
  sentDate: string;
  acceptedDate?: string;
  scoreEarned?: number;
  rewardUnlocked?: string;
}

export interface ReferrerProfile {
  referrerName: string;
  referrerAvatar: string;
  referrerLevel: number;
  referrerTier: string;
  totalInvitations: number;
  acceptedInvitations: number;
  pendingInvitations: number;
  declinedInvitations: number;
  conversionRate: number;
  totalScoreEarned: number;
  currentStreak: number;
  badges: string[];
  invitedEntities: string[];
}

export interface ReferralReward {
  id: string;
  type: RewardType;
  name: string;
  description: string;
  icon: string;
  emoji: string;
  color: string;
  bgColor: string;
  threshold: number;
  pointsValue: number;
  unlocked: boolean;
  progress: number;
  requirement: string;
}

export interface TopReferrer {
  position: number;
  name: string;
  avatar: string;
  level: number;
  tier: string;
  totalReferrals: number;
  acceptedReferrals: number;
  conversionRate: number;
  scoreEarned: number;
  badges: string[];
}

export interface ReferralScoreAction {
  action: string;
  points: number;
  icon: string;
  color: string;
  description: string;
}

export interface MostSharedEntity {
  id: string;
  type: string;
  name: string;
  subtitle: string;
  imageUrl: string;
  totalShares: number;
  weeklyShares: number;
  platformsBreakdown: Record<SharePlatform, number>;
  topPlatform: SharePlatform;
  linkUrl: string;
  scoreReward: number;
}

export interface ReferralDashboardStats {
  totalShares: number;
  weeklyShares: number;
  totalInvitations: number;
  acceptedInvitations: number;
  pendingInvitations: number;
  conversionRate: number;
  totalScoreGenerated: number;
  activeReferrers: number;
  topPlatform: SharePlatform;
  topPlatformShare: string;
}

// ─── SHARE PLATFORM CONFIG ──────────────────────────────

export const sharePlatforms: SharePlatformConfig[] = [
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: 'ri-whatsapp-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    description: 'Compartí directamente en WhatsApp con tus contactos.',
  },
  {
    id: 'facebook',
    label: 'Facebook',
    icon: 'ri-facebook-fill',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    description: 'Publicá en tu muro de Facebook o en grupos.',
  },
  {
    id: 'telegram',
    label: 'Telegram',
    icon: 'ri-telegram-line',
    color: 'text-sky-500',
    bgColor: 'bg-sky-50',
    description: 'Compartí en canales y chats de Telegram.',
  },
  {
    id: 'x',
    label: 'X',
    icon: 'ri-twitter-x-line',
    color: 'text-foreground-900',
    bgColor: 'bg-foreground-100',
    description: 'Twittealo y llega a toda tu audiencia.',
  },
  {
    id: 'qr',
    label: 'Código QR',
    icon: 'ri-qr-code-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    description: 'Generá un QR para compartir en eventos o impresos.',
  },
  {
    id: 'copy_link',
    label: 'Copiar Enlace',
    icon: 'ri-link',
    color: 'text-foreground-600',
    bgColor: 'bg-foreground-50',
    description: 'Copiá el enlace y pegalo donde quieras.',
  },
];

// ─── REFERRAL TYPE CONFIG ───────────────────────────────

export const referralTypes: ReferralTypeConfig[] = [
  {
    id: 'invitar_amigo',
    label: 'Invitar Amigo',
    icon: 'ri-user-add-line',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
    description: 'Invitá a un amigo a unirse a RFW. Ambos ganan puntos.',
    scoreReward: 250,
  },
  {
    id: 'invitar_oyente',
    label: 'Invitar Oyente',
    icon: 'ri-headphone-line',
    color: 'text-accent-600',
    bgColor: 'bg-accent-50',
    description: 'Invitá a otro oyente a descubrir RFW y sus comunidades.',
    scoreReward: 150,
  },
  {
    id: 'invitar_radio',
    label: 'Invitar Radio',
    icon: 'ri-radio-line',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    description: 'Invitá a una emisora a registrarse en RFW y crear su comunidad.',
    scoreReward: 500,
  },
  {
    id: 'invitar_locutor',
    label: 'Invitar Locutor',
    icon: 'ri-user-voice-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    description: 'Invitá a un locutor a crear su perfil y conectar con fans.',
    scoreReward: 350,
  },
];

// ─── REFERRAL STATUS CONFIG ─────────────────────────────

export const referralStatusConfig: Record<ReferralStatus, { label: string; icon: string; color: string; bgColor: string }> = {
  pendiente: { label: 'Pendiente', icon: 'ri-time-line', color: 'text-amber-600', bgColor: 'bg-amber-50' },
  aceptada: { label: 'Aceptada', icon: 'ri-check-line', color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
  rechazada: { label: 'Rechazada', icon: 'ri-close-line', color: 'text-red-500', bgColor: 'bg-red-50' },
  expirada: { label: 'Expirada', icon: 'ri-timer-line', color: 'text-foreground-400', bgColor: 'bg-foreground-100' },
};

// ─── REFERRAL RECORDS ────────────────────────────────────

export const referralRecords: ReferralRecord[] = [
  { id: 'ref-001', referralType: 'invitar_amigo', invitedName: 'Carlos Gutiérrez', invitedEmail: 'carlos.g@email.com', status: 'aceptada', sentDate: '2026-06-20T10:30:00Z', acceptedDate: '2026-06-20T14:22:00Z', scoreEarned: 250, rewardUnlocked: undefined },
  { id: 'ref-002', referralType: 'invitar_oyente', invitedName: 'María López', invitedEmail: 'maria.l@email.com', status: 'aceptada', sentDate: '2026-06-18T09:15:00Z', acceptedDate: '2026-06-18T16:40:00Z', scoreEarned: 150, rewardUnlocked: undefined },
  { id: 'ref-003', referralType: 'invitar_radio', invitedName: 'Radio La Nueva Onda', invitedEmail: 'contacto@lanuevaonda.com', status: 'aceptada', sentDate: '2026-06-15T08:00:00Z', acceptedDate: '2026-06-17T11:30:00Z', scoreEarned: 500, rewardUnlocked: 'Insignia: Embajador de Radios' },
  { id: 'ref-004', referralType: 'invitar_amigo', invitedName: 'Lucía Fernández', invitedEmail: 'lucia.f@email.com', status: 'pendiente', sentDate: '2026-06-21T07:45:00Z' },
  { id: 'ref-005', referralType: 'invitar_locutor', invitedName: 'Andrés Morales', invitedEmail: 'andres.m@email.com', status: 'aceptada', sentDate: '2026-06-12T14:20:00Z', acceptedDate: '2026-06-14T09:10:00Z', scoreEarned: 350, rewardUnlocked: 'Insignia: Conector de Talentos' },
  { id: 'ref-006', referralType: 'invitar_amigo', invitedName: 'Javier Méndez', invitedEmail: 'javier.m@email.com', status: 'rechazada', sentDate: '2026-06-19T16:00:00Z' },
  { id: 'ref-007', referralType: 'invitar_oyente', invitedName: 'Diana Rojas', invitedEmail: 'diana.r@email.com', status: 'aceptada', sentDate: '2026-06-10T11:00:00Z', acceptedDate: '2026-06-11T08:30:00Z', scoreEarned: 150, rewardUnlocked: undefined },
  { id: 'ref-008', referralType: 'invitar_amigo', invitedName: 'Pedro Castillo', invitedEmail: 'pedro.c@email.com', status: 'pendiente', sentDate: '2026-06-20T20:30:00Z' },
  { id: 'ref-009', referralType: 'invitar_radio', invitedName: 'Estéreo Vida FM', invitedEmail: 'info@estereovida.com', status: 'pendiente', sentDate: '2026-06-21T09:00:00Z' },
  { id: 'ref-010', referralType: 'invitar_locutor', invitedName: 'Gabriela Soto', invitedEmail: 'gabi.s@email.com', status: 'pendiente', sentDate: '2026-06-21T11:15:00Z' },
  { id: 'ref-011', referralType: 'invitar_amigo', invitedName: 'Ana Jiménez', invitedEmail: 'ana.j@email.com', status: 'aceptada', sentDate: '2026-06-05T13:00:00Z', acceptedDate: '2026-06-06T10:20:00Z', scoreEarned: 250, rewardUnlocked: undefined },
  { id: 'ref-012', referralType: 'invitar_amigo', invitedName: 'Roberto Díaz', invitedEmail: 'roberto.d@email.com', status: 'expirada', sentDate: '2026-05-30T09:00:00Z' },
  { id: 'ref-013', referralType: 'invitar_oyente', invitedName: 'Sofía Martínez', invitedEmail: 'sofia.m@email.com', status: 'aceptada', sentDate: '2026-06-01T10:00:00Z', acceptedDate: '2026-06-02T15:45:00Z', scoreEarned: 150, rewardUnlocked: undefined },
  { id: 'ref-014', referralType: 'invitar_radio', invitedName: 'Radio Pasión Tropical', invitedEmail: 'contacto@pasiontropical.fm', status: 'aceptada', sentDate: '2026-06-08T08:30:00Z', acceptedDate: '2026-06-10T14:00:00Z', scoreEarned: 500, rewardUnlocked: 'Insignia: Constructor de Comunidades' },
  { id: 'ref-015', referralType: 'invitar_amigo', invitedName: 'Elena Vargas', invitedEmail: 'elena.v@email.com', status: 'pendiente', sentDate: '2026-06-21T08:30:00Z' },
  { id: 'ref-016', referralType: 'invitar_locutor', invitedName: 'Martín Suárez', invitedEmail: 'martin.s@email.com', status: 'rechazada', sentDate: '2026-06-16T12:00:00Z' },
  { id: 'ref-017', referralType: 'invitar_oyente', invitedName: 'Pablo Núñez', invitedEmail: 'pablo.n@email.com', status: 'aceptada', sentDate: '2026-06-03T15:30:00Z', acceptedDate: '2026-06-04T09:15:00Z', scoreEarned: 150, rewardUnlocked: undefined },
  { id: 'ref-018', referralType: 'invitar_amigo', invitedName: 'Carla Ríos', invitedEmail: 'carla.r@email.com', status: 'aceptada', sentDate: '2026-06-17T11:45:00Z', acceptedDate: '2026-06-18T08:30:00Z', scoreEarned: 250, rewardUnlocked: undefined },
  { id: 'ref-019', referralType: 'invitar_radio', invitedName: 'FM Horizonte', invitedEmail: 'admin@fmhorizonte.org', status: 'pendiente', sentDate: '2026-06-19T10:00:00Z' },
  { id: 'ref-020', referralType: 'invitar_amigo', invitedName: 'Tomás Herrera', invitedEmail: 'tomas.h@email.com', status: 'pendiente', sentDate: '2026-06-21T06:00:00Z' },
];

// ─── REFERRER PROFILE ────────────────────────────────────

export const referrerProfile: ReferrerProfile = {
  referrerName: 'Marco Rivera',
  referrerAvatar: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20street%20style%20portrait%2C%20casual%20genuine%20smile%2C%20warm%20urban%20background%2C%20lifestyle%20editorial%20photography%2C%20natural%20light%2C%20approachable%20look&width=120&height=120&seq=share-marco-rivera&orientation=squarish',
  referrerLevel: 4,
  referrerTier: 'Embajador RFW',
  totalInvitations: 20,
  acceptedInvitations: 10,
  pendingInvitations: 7,
  declinedInvitations: 2,
  conversionRate: 50.0,
  totalScoreEarned: 2975,
  currentStreak: 4,
  badges: ['Embajador de Radios', 'Conector de Talentos', 'Constructor de Comunidades'],
  invitedEntities: ['Radio La Nueva Onda', 'Radio Pasión Tropical'],
};

// ─── REWARDS CATALOG ────────────────────────────────────

export const referralRewards: ReferralReward[] = [
  {
    id: 'reward-001',
    type: 'puntos',
    name: 'Bonus de Bienvenida',
    description: 'Sumás 100 puntos extras por tu primer referido exitoso.',
    icon: 'ri-flashlight-line',
    emoji: '⚡',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
    threshold: 1,
    pointsValue: 100,
    unlocked: true,
    progress: 100,
    requirement: '1 referido exitoso',
  },
  {
    id: 'reward-002',
    type: 'insignia',
    name: 'Embajador de Radios',
    description: 'Insignia por traer 2 radios a la plataforma RFW.',
    icon: 'ri-radio-line',
    emoji: '📻',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    threshold: 2,
    pointsValue: 0,
    unlocked: true,
    progress: 100,
    requirement: '2 radios referidas',
  },
  {
    id: 'reward-003',
    type: 'insignia',
    name: 'Conector de Talentos',
    description: 'Insignia por traer 1 locutor a RFW.',
    icon: 'ri-user-voice-line',
    emoji: '🎙️',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    threshold: 1,
    pointsValue: 0,
    unlocked: true,
    progress: 100,
    requirement: '1 locutor referido',
  },
  {
    id: 'reward-004',
    type: 'insignia',
    name: 'Constructor de Comunidades',
    description: 'Insignia por alcanzar 5 referidos en total.',
    icon: 'ri-building-2-line',
    emoji: '🏗️',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    threshold: 5,
    pointsValue: 0,
    unlocked: true,
    progress: 100,
    requirement: '5 referidos exitosos',
  },
  {
    id: 'reward-005',
    type: 'beneficio',
    name: 'Acceso Anticipado',
    description: 'Acceso a features nuevas de RFW antes que nadie.',
    icon: 'ri-rocket-line',
    emoji: '🚀',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    threshold: 10,
    pointsValue: 0,
    unlocked: true,
    progress: 100,
    requirement: '10 referidos exitosos',
  },
  {
    id: 'reward-006',
    type: 'vip',
    name: 'Status VIP',
    description: 'Status VIP por 3 meses con todos los beneficios premium.',
    icon: 'ri-vip-crown-line',
    emoji: '👑',
    color: 'text-accent-600',
    bgColor: 'bg-accent-50',
    threshold: 15,
    pointsValue: 0,
    unlocked: false,
    progress: 66.7,
    requirement: '15 referidos exitosos',
  },
  {
    id: 'reward-007',
    type: 'puntos',
    name: 'Mega Bonus',
    description: 'Sumás 500 puntos extras cuando llegás a 20 referidos.',
    icon: 'ri-fire-line',
    emoji: '🔥',
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    threshold: 20,
    pointsValue: 500,
    unlocked: false,
    progress: 50,
    requirement: '20 referidos exitosos',
  },
  {
    id: 'reward-008',
    type: 'insignia',
    name: 'Leyenda de la Comunidad',
    description: 'La insignia más prestigiosa. Solo para los que trajeron 25+ referidos.',
    icon: 'ri-star-smile-line',
    emoji: '🌟',
    color: 'text-accent-600',
    bgColor: 'bg-accent-100',
    threshold: 25,
    pointsValue: 0,
    unlocked: false,
    progress: 40,
    requirement: '25 referidos exitosos',
  },
];

// ─── TOP REFERRERS ──────────────────────────────────────

export const topReferrers: TopReferrer[] = [
  {
    position: 1,
    name: 'Isabella Torres',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20portrait%2C%20confident%20warm%20smile%2C%20clean%20studio%20background%2C%20soft%20natural%20lighting%2C%20editorial%20headshot%20photography&width=120&height=120&seq=share-isabella-torres&orientation=squarish',
    level: 6,
    tier: 'Leyenda RFW',
    totalReferrals: 42,
    acceptedReferrals: 38,
    conversionRate: 90.5,
    scoreEarned: 12400,
    badges: ['Embajador de Radios', 'Constructor de Comunidades', 'Leyenda de la Comunidad'],
  },
  {
    position: 2,
    name: 'Felipe Gutiérrez',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20professional%20portrait%2C%20friendly%20approachable%20expression%2C%20clean%20modern%20studio%20background%2C%20natural%20daylight%2C%20lifestyle%20headshot&width=120&height=120&seq=share-felipe-gutierrez&orientation=squarish',
    level: 5,
    tier: 'Embajador RFW',
    totalReferrals: 35,
    acceptedReferrals: 31,
    conversionRate: 88.6,
    scoreEarned: 9800,
    badges: ['Embajador de Radios', 'Conector de Talentos'],
  },
  {
    position: 3,
    name: 'Adriana Mejía',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20portrait%2C%20warm%20genuine%20smile%2C%20soft%20studio%20lighting%2C%20elegant%20casual%20look%2C%20editorial%20photography%2C%20approachable%20confidence&width=120&height=120&seq=share-adriana-mejia&orientation=squarish',
    level: 5,
    tier: 'Embajador RFW',
    totalReferrals: 28,
    acceptedReferrals: 25,
    conversionRate: 89.3,
    scoreEarned: 8600,
    badges: ['Constructor de Comunidades', 'Conector de Talentos'],
  },
  {
    position: 4,
    name: 'Ricky Navarro',
    avatar: 'https://readdy.ai/api/search-image?query=Charismatic%20Latino%20radio%20host%20portrait%2C%20warm%20smile%2C%20professional%20studio%20with%20microphones%2C%20golden%20hour%20lighting%2C%20clean%20editorial%20photography&width=120&height=120&seq=share-ricky-navarro&orientation=squarish',
    level: 4,
    tier: 'Top Fan',
    totalReferrals: 22,
    acceptedReferrals: 19,
    conversionRate: 86.4,
    scoreEarned: 7200,
    badges: ['Embajador de Radios', 'Acceso Anticipado'],
  },
  {
    position: 5,
    name: 'Marco Rivera',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20street%20style%20portrait%2C%20casual%20genuine%20smile%2C%20warm%20urban%20background%2C%20lifestyle%20editorial%20photography%2C%20natural%20light%2C%20approachable%20look&width=120&height=120&seq=share-marco-top&orientation=squarish',
    level: 4,
    tier: 'Top Fan',
    totalReferrals: 20,
    acceptedReferrals: 10,
    conversionRate: 50.0,
    scoreEarned: 2975,
    badges: ['Embajador de Radios', 'Conector de Talentos'],
  },
  {
    position: 6,
    name: 'Sofía Linares',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Latina%20radio%20DJ%20portrait%2C%20trendy%20street%20style%2C%20urban%20studio%20with%20neon%20lights%2C%20vibrant%20energetic%20atmosphere%2C%20editorial%20lifestyle%20photography&width=120&height=120&seq=share-sofia-linares&orientation=squarish',
    level: 4,
    tier: 'Top Fan',
    totalReferrals: 17,
    acceptedReferrals: 14,
    conversionRate: 82.4,
    scoreEarned: 5100,
    badges: ['Constructor de Comunidades'],
  },
  {
    position: 7,
    name: 'Valentina Ríos',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20radio%20host%20portrait%2C%20confident%20warm%20smile%2C%20studio%20with%20microphones%2C%20golden%20hour%20lighting%2C%20clean%20editorial%20photography&width=120&height=120&seq=share-valentina-rios&orientation=squarish',
    level: 4,
    tier: 'Top Fan',
    totalReferrals: 15,
    acceptedReferrals: 13,
    conversionRate: 86.7,
    scoreEarned: 4800,
    badges: ['Embajador de Radios'],
  },
  {
    position: 8,
    name: 'Carlos Mendoza',
    avatar: 'https://readdy.ai/api/search-image?query=Latino%20sports%20commentator%20portrait%2C%20energetic%20expression%2C%20professional%20broadcast%20studio%2C%20dynamic%20lighting%2C%20clean%20editorial%20photography&width=120&height=120&seq=share-carlos-mendoza&orientation=squarish',
    level: 3,
    tier: 'Fan Activo',
    totalReferrals: 12,
    acceptedReferrals: 10,
    conversionRate: 83.3,
    scoreEarned: 3500,
    badges: ['Conector de Talentos'],
  },
  {
    position: 9,
    name: 'Diego Montenegro',
    avatar: 'https://readdy.ai/api/search-image?query=Latino%20comedian%20radio%20host%20portrait%2C%20humorous%20expression%2C%20casual%20relaxed%20studio%20vibe%2C%20warm%20atmospheric%20lighting%2C%20authentic%20personality%20photography&width=120&height=120&seq=share-diego-montenegro&orientation=squarish',
    level: 3,
    tier: 'Fan Activo',
    totalReferrals: 10,
    acceptedReferrals: 8,
    conversionRate: 80.0,
    scoreEarned: 2800,
    badges: ['Constructor de Comunidades'],
  },
  {
    position: 10,
    name: 'Roberto Aguilar',
    avatar: 'https://readdy.ai/api/search-image?query=Mature%20Latino%20radio%20host%20portrait%2C%20distinguished%20silver%20hair%2C%20warm%20professional%20studio%20setting%2C%20classic%20broadcast%20atmosphere%2C%20authoritative%20dignified%20photography&width=120&height=120&seq=share-roberto-aguilar&orientation=squarish',
    level: 3,
    tier: 'Fan Activo',
    totalReferrals: 8,
    acceptedReferrals: 7,
    conversionRate: 87.5,
    scoreEarned: 2500,
    badges: ['Embajador de Radios'],
  },
];

// ─── MOST SHARED ENTITIES ───────────────────────────────

export const mostSharedEntities: MostSharedEntity[] = [
  {
    id: 'shared-001',
    type: 'Radio',
    name: 'Banda Radio MX',
    subtitle: '103.50 FM · Regional Mexicano',
    imageUrl: 'https://readdy.ai/api/search-image?query=Banda%20music%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20brass%20instruments%20and%20Mexican%20motifs%2C%20bold%20red%20white%20and%20green%20colors%2C%20festive%20regional%20brand%20identity&width=200&height=200&seq=share-banda-mx&orientation=squarish',
    totalShares: 48700,
    weeklyShares: 5240,
    platformsBreakdown: { whatsapp: 23400, facebook: 12800, telegram: 5400, x: 3100, qr: 1800, copy_link: 2200 },
    topPlatform: 'whatsapp',
    linkUrl: '/radio',
    scoreReward: 150,
  },
  {
    id: 'shared-002',
    type: 'Programa',
    name: 'Mañanas Argentinas',
    subtitle: 'Lun-Vie · 6:00-10:00 AM',
    imageUrl: 'https://readdy.ai/api/search-image?query=Morning%20show%20program%20logo%20design%2C%20circular%20warm%20sunrise%20colors%20with%20coffee%20cup%20motif%2C%20friendly%20inviting%20broadcast%20brand%20identity%2C%20clean%20modern%20aesthetic&width=200&height=200&seq=share-mañanas-argentinas&orientation=squarish',
    totalShares: 32100,
    weeklyShares: 3800,
    platformsBreakdown: { whatsapp: 15200, facebook: 8900, telegram: 3400, x: 2100, qr: 1200, copy_link: 1300 },
    topPlatform: 'whatsapp',
    linkUrl: '/program/mañanas-argentinas',
    scoreReward: 120,
  },
  {
    id: 'shared-003',
    type: 'Locutor',
    name: 'Valentina Ríos',
    subtitle: 'Locutora · Radio Nueva Actitud',
    imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20radio%20host%20portrait%2C%20confident%20warm%20smile%2C%20studio%20with%20microphones%2C%20golden%20hour%20lighting%2C%20clean%20editorial%20photography&width=200&height=200&seq=share-valentina-rios-top&orientation=squarish',
    totalShares: 28400,
    weeklyShares: 4100,
    platformsBreakdown: { whatsapp: 13200, facebook: 7800, telegram: 3200, x: 1900, qr: 1100, copy_link: 1200 },
    topPlatform: 'whatsapp',
    linkUrl: '/host/valentina-rios',
    scoreReward: 130,
  },
  {
    id: 'shared-004',
    type: 'Evento',
    name: 'Festival RFW Monterrey 2026',
    subtitle: '15 Jul 2026 · Parque Fundidora',
    imageUrl: 'https://readdy.ai/api/search-image?query=Massive%20outdoor%20music%20festival%20crowd%2C%20colorful%20stage%20lights%20and%20confetti%2C%20energetic%20concert%20atmosphere%20at%20dusk%2C%20wide%20angle%20festival%20photography%2C%20vibrant%20celebratory%20mood&width=320&height=180&seq=share-festival-monterrey&orientation=landscape',
    totalShares: 24700,
    weeklyShares: 6100,
    platformsBreakdown: { whatsapp: 10100, facebook: 7800, telegram: 2800, x: 2100, qr: 800, copy_link: 1100 },
    topPlatform: 'whatsapp',
    linkUrl: '/eventos',
    scoreReward: 80,
  },
  {
    id: 'shared-005',
    type: 'Concurso',
    name: 'La Voz de la Banda',
    subtitle: 'Canto Regional · Premio $10,000 MXN',
    imageUrl: 'https://readdy.ai/api/search-image?query=Singing%20competition%20stage%2C%20spotlight%20on%20microphone%20center%20stage%2C%20dramatic%20red%20and%20gold%20lighting%2C%20talent%20show%20atmosphere%2C%20professional%20performance%20photography%2C%20grand%20theatrical%20setting&width=320&height=180&seq=share-voz-banda&orientation=landscape',
    totalShares: 19800,
    weeklyShares: 3400,
    platformsBreakdown: { whatsapp: 8900, facebook: 5200, telegram: 2400, x: 1500, qr: 700, copy_link: 1100 },
    topPlatform: 'whatsapp',
    linkUrl: '/competencias',
    scoreReward: 90,
  },
  {
    id: 'shared-006',
    type: 'Club',
    name: 'Banda MX Army',
    subtitle: 'Club Oficial · 12,400 miembros',
    imageUrl: 'https://readdy.ai/api/search-image?query=Exclusive%20fan%20club%20logo%20design%2C%20circular%20emblem%20with%20crown%20and%20shield%20motifs%2C%20royal%20gold%20and%20crimson%20colors%2C%20premium%20community%20brand%20identity%2C%20elegant%20luxurious%20aesthetic&width=200&height=200&seq=share-banda-mx-army&orientation=squarish',
    totalShares: 15200,
    weeklyShares: 2100,
    platformsBreakdown: { whatsapp: 6800, facebook: 4200, telegram: 1800, x: 1100, qr: 600, copy_link: 700 },
    topPlatform: 'whatsapp',
    linkUrl: '/clubes',
    scoreReward: 80,
  },
  {
    id: 'shared-007',
    type: 'Trivia',
    name: 'Trivia de Música: Décadas',
    subtitle: '20 preguntas · 40 min · Torneo',
    imageUrl: 'https://readdy.ai/api/search-image?query=Music%20trivia%20game%20show%20set%2C%20colorful%20neon%20buzzers%20and%20scoreboard%2C%20retro%20vinyl%20records%20decoration%2C%20energetic%20game%20atmosphere%2C%20bright%20studio%20lighting%2C%20fun%20competitive%20vibe&width=320&height=180&seq=share-trivia-musica&orientation=landscape',
    totalShares: 12300,
    weeklyShares: 2800,
    platformsBreakdown: { whatsapp: 5400, facebook: 3200, telegram: 1600, x: 900, qr: 500, copy_link: 700 },
    topPlatform: 'whatsapp',
    linkUrl: '/trivias',
    scoreReward: 70,
  },
  {
    id: 'shared-008',
    type: 'Encuesta',
    name: '¿Cuál es la mejor radio de Regional Mexicano?',
    subtitle: 'Votación Rápida · Cierra en 2h',
    imageUrl: 'https://readdy.ai/api/search-image?query=Interactive%20poll%20voting%20interface%2C%20glowing%20percentage%20bars%20on%20dark%20background%2C%20community%20engagement%20visualization%2C%20modern%20digital%20dashboard%2C%20clean%20data%20visualization%20design%2C%20tech%20aesthetic&width=320&height=180&seq=share-encuesta-regional&orientation=landscape',
    totalShares: 9800,
    weeklyShares: 4200,
    platformsBreakdown: { whatsapp: 4200, facebook: 2800, telegram: 1100, x: 800, qr: 400, copy_link: 500 },
    topPlatform: 'whatsapp',
    linkUrl: '/encuestas',
    scoreReward: 50,
  },
];

// ─── SCORE ACTIONS ──────────────────────────────────────

export const referralScoreActions: ReferralScoreAction[] = [
  {
    action: 'Invitar Amigo (Aceptada)',
    points: 250,
    icon: 'ri-user-add-line',
    color: 'text-primary-600',
    description: 'Sumás 250 puntos por cada amigo que acepta tu invitación y se une a RFW.',
  },
  {
    action: 'Invitar Oyente (Aceptada)',
    points: 150,
    icon: 'ri-headphone-line',
    color: 'text-accent-600',
    description: 'Sumás 150 puntos por cada oyente que acepta tu invitación.',
  },
  {
    action: 'Invitar Radio (Aceptada)',
    points: 500,
    icon: 'ri-radio-line',
    color: 'text-pink-600',
    description: 'Sumás 500 puntos cuando una radio que invitaste se registra en RFW.',
  },
  {
    action: 'Invitar Locutor (Aceptada)',
    points: 350,
    icon: 'ri-user-voice-line',
    color: 'text-amber-600',
    description: 'Sumás 350 puntos cuando un locutor que invitaste crea su perfil.',
  },
  {
    action: 'Compartir Contenido',
    points: 15,
    icon: 'ri-share-forward-line',
    color: 'text-emerald-600',
    description: 'Sumás 15 puntos por cada vez que compartís contenido de RFW en cualquier plataforma.',
  },
  {
    action: 'Compartir con QR',
    points: 20,
    icon: 'ri-qr-code-line',
    color: 'text-violet-600',
    description: 'Sumás 20 puntos cuando alguien escanea tu QR y se une a RFW.',
  },
  {
    action: 'Racha de Referidos',
    points: 100,
    icon: 'ri-fire-line',
    color: 'text-red-500',
    description: 'Sumás 100 puntos bonus por cada semana consecutiva con al menos 1 referido exitoso.',
  },
];

// ─── DASHBOARD STATS ────────────────────────────────────

export const shareDashboardStats: ReferralDashboardStats = {
  totalShares: 2847000,
  weeklyShares: 142800,
  totalInvitations: 48700,
  acceptedInvitations: 31200,
  pendingInvitations: 12400,
  conversionRate: 64.1,
  totalScoreGenerated: 12460000,
  activeReferrers: 8920,
  topPlatform: 'whatsapp',
  topPlatformShare: '48.4%',
};

// ─── HELPERS ────────────────────────────────────────────

export function getSharePlatformById(id: SharePlatform): SharePlatformConfig | undefined {
  return sharePlatforms.find((p) => p.id === id);
}

export function getReferralTypeById(id: ReferralType): ReferralTypeConfig | undefined {
  return referralTypes.find((t) => t.id === id);
}

export function getReferralRecordsByStatus(status: ReferralStatus | 'todos'): ReferralRecord[] {
  if (status === 'todos') return referralRecords;
  return referralRecords.filter((r) => r.status === status);
}

export function getAcceptedReferrals(): ReferralRecord[] {
  return referralRecords.filter((r) => r.status === 'aceptada');
}

export function getPendingReferrals(): ReferralRecord[] {
  return referralRecords.filter((r) => r.status === 'pendiente');
}

export function getMostShared(limit = 8): MostSharedEntity[] {
  return [...mostSharedEntities].sort((a, b) => b.totalShares - a.totalShares).slice(0, limit);
}

export function getTopReferrersList(limit = 10): TopReferrer[] {
  return topReferrers.slice(0, limit);
}

export function getUnlockedRewards(): ReferralReward[] {
  return referralRewards.filter((r) => r.unlocked);
}

export function getLockedRewards(): ReferralReward[] {
  return referralRewards.filter((r) => !r.unlocked);
}

export function getRewardById(id: string): ReferralReward | undefined {
  return referralRewards.find((r) => r.id === id);
}

export function formatShareCount(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

export function formatConversionRate(num: number): string {
  return `${num.toFixed(1)}%`;
}

export function getPlatformIcon(platform: SharePlatform): string {
  return sharePlatforms.find((p) => p.id === platform)?.icon || 'ri-share-line';
}

export function getPlatformLabel(platform: SharePlatform): string {
  return sharePlatforms.find((p) => p.id === platform)?.label || platform;
}

export function getPlatformColor(platform: SharePlatform): string {
  return sharePlatforms.find((p) => p.id === platform)?.color || 'text-foreground-500';
}